/**
 * Sstv2MigrationEvidenceRun entity (DEV-193 — synthetic migration evidence).
 *
 * A "synthetic migration evidence run" drives the SirSteward (SST) V1→V2 locker
 * migration endpoints end-to-end against the shared `ssdev` demo locker, then
 * proves — from independent CloudWatch evidence — that the expected `migration:*`
 * events were published to the SQS queue. It is the UI-triggered, backend-driven
 * counterpart of the `apps/sl2/scripts/sstv2MigrationEvidence.ts` CLI script.
 *
 * One DDB record per run (append-only; runId is random, not deterministic).
 */
export const Sstv2MigrationEvidenceRunThingTypeMrn =
  "mrn:sl2:::sstv2MigrationEvidenceRun";

/** Overall run lifecycle. */
export const Sstv2EvidenceRunStatusList = [
  "PENDING", // created, orchestrator not yet started
  "RUNNING", // orchestrator working through the steps
  "COMPLETED", // all steps + evidence gathered
  "FAILED", // orchestrator threw
  "CANCELLED", // released by the user (terminal; frees the singleton lock)
] as const;
export type Sstv2EvidenceRunStatus =
  (typeof Sstv2EvidenceRunStatusList)[number];

/** Per-step state, mirrored in the UI stepper. */
export const Sstv2EvidenceStepStateList = [
  "pending",
  "running",
  "done",
  "error",
] as const;
export type Sstv2EvidenceStepState =
  (typeof Sstv2EvidenceStepStateList)[number];

/** A run is "active" (holds the singleton lock) in these statuses. */
export const Sstv2EvidenceActiveStatuses: ReadonlySet<Sstv2EvidenceRunStatus> =
  new Set<Sstv2EvidenceRunStatus>(["PENDING", "RUNNING"]);

export const isActiveEvidenceRunStatus = (s: string): boolean =>
  Sstv2EvidenceActiveStatuses.has(s as Sstv2EvidenceRunStatus);

/**
 * The fixed 6-step protocol (shared FE/BE so the stepper and the orchestrator
 * agree). Order + methods + expected events mirror the CLI evidence script.
 * `needsUuid` steps are skipped until `create_locker` harvests the V2 locker
 * uuid. `isStatus` steps are GETs that emit no SQS events (the HTTP response is
 * the signal).
 */
export const SSTV2_EVIDENCE_STEP_DEFS = [
  {
    key: "create_locker",
    title: "Create V2 locker + migrate ACTIVE reservations",
    method: "POST",
    needsUuid: false,
    isStatus: false,
    expectedEvents: [
      "migration:locker:migrated",
      "migration:reservations:active:done",
    ],
  },
  {
    key: "history_migration",
    title: "Migrate reservation history",
    method: "POST",
    needsUuid: true,
    isStatus: false,
    expectedEvents: ["migration:reservations:history:completed"],
  },
  {
    key: "local_migration",
    title: "Cutover / official sign-off",
    method: "POST",
    needsUuid: true,
    isStatus: false,
    expectedEvents: ["migration:locker:operational"],
  },
  {
    key: "status_after_operational",
    title: "Status after cutover",
    method: "GET",
    needsUuid: true,
    isStatus: true,
    expectedEvents: [],
  },
  {
    key: "rollback",
    title: "Rollback",
    method: "POST",
    needsUuid: true,
    isStatus: false,
    expectedEvents: [
      "migration:rollback:started",
      "migration:rollback:done",
    ],
  },
  {
    key: "status_after_rollback",
    title: "Status after rollback",
    method: "GET",
    needsUuid: true,
    isStatus: true,
    expectedEvents: [],
  },
] as const;

export type Sstv2EvidenceStepKey =
  (typeof SSTV2_EVIDENCE_STEP_DEFS)[number]["key"];

/** The two steps that make up the rollback half of the protocol. */
export const SSTV2_EVIDENCE_ROLLBACK_STEP_KEYS: readonly Sstv2EvidenceStepKey[] =
  ["rollback", "status_after_rollback"] as const;

/**
 * Step defs for a run. When `runRollback` is false the run stops after the
 * `local_migration` (cutover) step, so the created V2 locker PERSISTS in the
 * chosen tenant instead of being torn back down — the rollback + its
 * status-check step are dropped. Shared FE/BE so the stepper and the
 * orchestrator agree on the step set for a given run.
 */
export const evidenceStepDefsFor = (runRollback: boolean) =>
  runRollback
    ? SSTV2_EVIDENCE_STEP_DEFS
    : SSTV2_EVIDENCE_STEP_DEFS.filter(
        (d) => !SSTV2_EVIDENCE_ROLLBACK_STEP_KEYS.includes(d.key),
      );

// ---------------------------------------------------------------------------
// Reservation tagging
// ---------------------------------------------------------------------------

/**
 * SSM parameter holding the SST reservation `tag`. This is the SAME parameter
 * `reservationApi.createReservation` reads for real reservations, so seeded demo
 * reservations are tagged exactly the way SL2 tags its own (dev resolves to
 * "V2-dev"). Reading it — rather than hardcoding — keeps the value correct per
 * environment and prevents drift from the production write path.
 *
 * `tag` IS a real, persisted v1 field: `POST /api/v1/reservations/` accepts it
 * and the API returns it (untagged reservations come back as "V1"). It is absent
 * from the generated `sst-openapi-spec.d.ts` only because that file is stale.
 */
export const SSTV2_RESERVATION_TAG_SSM_PARAM = "/sl2/sst/reservation-tag";

/** Value the SSM parameter resolves to in dev; fallback when SSM is unreachable. */
export const SSTV2_RESERVATION_TAG_FALLBACK = "V2-dev";

/**
 * Marker written into `meta_data.seedTag` by the seeders.
 *
 * Deliberately NOT the `tag` field: `tag` is now the same value production uses
 * for every real reservation, so it cannot identify *seeded* rows. Teardown
 * selects on this instead — matching on `tag` would sweep real reservations.
 */
export const SSTV2_EVIDENCE_SEED_MARKER = "dev-193-evidence-seed";

// ---------------------------------------------------------------------------
// Reseed plan — how many demo reservations a run creates, and in what statuses
// ---------------------------------------------------------------------------

/**
 * Reservation statuses the reseed can produce on the SL1 demo locker.
 *
 * `DRAFT` is excluded (it is the transient pre-confirm state, reaped by the
 * partner) and so is `DELETED` (that is teardown, not a fixture).
 */
export const SSTV2_EVIDENCE_SEED_STATUS_LIST = [
  "CONFIRMED",
  "DELIVERED",
  "CLOSE",
  "CANCEL",
  "EXPIRED",
] as const;
export type Sstv2EvidenceSeedStatus =
  (typeof SSTV2_EVIDENCE_SEED_STATUS_LIST)[number];

/** Exact per-status counts for one reseed. */
export type Sstv2EvidenceSeedPlan = Record<Sstv2EvidenceSeedStatus, number>;

/**
 * Statuses that leave the reservation ACTIVE. Each one holds its locker door for
 * the whole run, so their combined count can never exceed the locker's available
 * door count — unlike CLOSE/CANCEL/EXPIRED, whose doors the seeder recycles.
 * These are also the reservations `create_locker` migrates; the rest are history.
 */
export const SSTV2_EVIDENCE_ACTIVE_SEED_STATUSES: readonly Sstv2EvidenceSeedStatus[] =
  ["CONFIRMED", "DELIVERED"] as const;

export const isActiveEvidenceSeedStatus = (s: Sstv2EvidenceSeedStatus) =>
  SSTV2_EVIDENCE_ACTIVE_SEED_STATUSES.includes(s);

/** Default total when the operator does not set a per-status distribution. */
export const SSTV2_EVIDENCE_DEFAULT_RESERVATION_COUNT = 10;
/** Default value pre-filled in each field when a custom distribution is opened. */
export const SSTV2_EVIDENCE_DEFAULT_PER_STATUS_COUNT = 10;
/**
 * Upper bound on one reseed. Chosen so the reseed stays well inside the
 * orchestrator's 900 s budget (~2 partner calls per reservation, concurrency-
 * capped) and so a mis-typed count cannot flood the shared ssdev backend.
 */
export const SSTV2_EVIDENCE_MAX_RESERVATION_COUNT = 500;

/**
 * Mix used to split a plain total across statuses. Weighted so the default N=10
 * still yields the 3 ACTIVE reservations the original fixed 7-item plan produced
 * (2 CONFIRMED + 1 DELIVERED) — `create_locker` needs actives to have anything to
 * migrate — with the rest as history for `history_migration`.
 */
const SSTV2_EVIDENCE_SEED_WEIGHTS: Sstv2EvidenceSeedPlan = {
  CONFIRMED: 2,
  DELIVERED: 1,
  CLOSE: 3,
  CANCEL: 2,
  EXPIRED: 2,
};

export const emptyEvidenceSeedPlan = (): Sstv2EvidenceSeedPlan => ({
  CONFIRMED: 0,
  DELIVERED: 0,
  CLOSE: 0,
  CANCEL: 0,
  EXPIRED: 0,
});

export const evidenceSeedPlanTotal = (plan: Sstv2EvidenceSeedPlan): number =>
  SSTV2_EVIDENCE_SEED_STATUS_LIST.reduce((a, s) => a + (plan[s] || 0), 0);

/** Every field pre-filled with the per-status default (the UI's starting point). */
export const defaultEvidenceSeedDistribution = (): Sstv2EvidenceSeedPlan =>
  SSTV2_EVIDENCE_SEED_STATUS_LIST.reduce(
    (acc, s) => ({ ...acc, [s]: SSTV2_EVIDENCE_DEFAULT_PER_STATUS_COUNT }),
    emptyEvidenceSeedPlan(),
  );

/**
 * Split `total` across the statuses using the default weights, largest-remainder
 * so the parts sum to exactly `total` (plain rounding drifts).
 */
export const distributeEvidenceSeedCount = (
  total: number,
): Sstv2EvidenceSeedPlan => {
  const plan = emptyEvidenceSeedPlan();
  if (total <= 0) return plan;

  const weightSum = evidenceSeedPlanTotal(SSTV2_EVIDENCE_SEED_WEIGHTS);
  const exact = SSTV2_EVIDENCE_SEED_STATUS_LIST.map((s) => ({
    s,
    raw: (SSTV2_EVIDENCE_SEED_WEIGHTS[s] / weightSum) * total,
  }));
  let assigned = 0;
  for (const e of exact) {
    plan[e.s] = Math.floor(e.raw);
    assigned += plan[e.s];
  }
  exact
    .slice()
    .sort((a, b) => (b.raw % 1) - (a.raw % 1))
    .slice(0, total - assigned)
    .forEach((e) => plan[e.s]++);
  return plan;
};

const clampCount = (v: unknown): number => {
  const n = Math.floor(Number(v));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, SSTV2_EVIDENCE_MAX_RESERVATION_COUNT);
};

/**
 * Turn operator input into the exact plan the reseed will execute. Shared FE/BE
 * so the tab's preview and the orchestrator can never disagree, and so the
 * backend re-validates rather than trusting the request body.
 *
 * - `distribution` present with any positive count wins; `reservationCount` is
 *   then ignored (the total IS the sum of the parts).
 * - Otherwise `reservationCount` is split by the default weights.
 * - Anything absent, negative, non-numeric or over the cap is clamped, never
 *   throws — a bad body degrades to the default rather than failing the run.
 */
export const resolveEvidenceSeedPlan = (input?: {
  reservationCount?: unknown;
  distribution?: Partial<Record<string, unknown>> | null;
}): Sstv2EvidenceSeedPlan => {
  const raw = input?.distribution;
  if (raw && typeof raw === "object") {
    const plan = emptyEvidenceSeedPlan();
    for (const s of SSTV2_EVIDENCE_SEED_STATUS_LIST) plan[s] = clampCount(raw[s]);
    let total = evidenceSeedPlanTotal(plan);
    if (total > 0) {
      // Scale down proportionally if the parts together blow the cap.
      if (total > SSTV2_EVIDENCE_MAX_RESERVATION_COUNT) {
        const factor = SSTV2_EVIDENCE_MAX_RESERVATION_COUNT / total;
        for (const s of SSTV2_EVIDENCE_SEED_STATUS_LIST) {
          plan[s] = Math.floor(plan[s] * factor);
        }
        total = evidenceSeedPlanTotal(plan);
      }
      return plan;
    }
    // An all-zero distribution means "no override" — fall through to the total.
  }

  const requested =
    input?.reservationCount === undefined || input?.reservationCount === null
      ? SSTV2_EVIDENCE_DEFAULT_RESERVATION_COUNT
      : clampCount(input.reservationCount);
  return distributeEvidenceSeedCount(requested);
};

/**
 * Shape of an SL1 (V1) locker id — 24 lowercase hex chars, Mongo-style
 * (`docs/sstv2-migration-mechanism.md:450`).
 *
 * The SL1 source locker a run drives is **operator-supplied per run** and has no
 * default anywhere on the runtime path (LOCKER-344): the QA and prod lockers are
 * different ids from the `ssdev` demo one, and a silent fallback is precisely how
 * a QA run would end up driving the shared `ssdev` DEMO-MIGRATION locker. Both
 * tiers validate with this — the FE to disable the button, the backend to reject
 * the request — so the operator sees the problem before the run starts.
 */
export const SSTV2_SL1_LOCKER_ID_PATTERN = /^[0-9a-f]{24}$/;

/** True when `value` is shaped like an SL1 locker id. Shape only — never existence. */
export const isSstv1LockerId = (value: unknown): value is string =>
  typeof value === "string" && SSTV2_SL1_LOCKER_ID_PATTERN.test(value.trim());

/**
 * Dedicated, obviously-synthetic tenant that owns the pre-existing SL2 "V1"
 * Locker Thing for the demo locker. Isolating the fixture here keeps it out of
 * any real customer's fleet while still exercising the real consumer path
 * (resolve V1 locker → fold Sstv2Migration → create V2 Thing on operational).
 */
export const SSTV2_EVIDENCE_FIXTURE_TENANT = "sstv2-evidence";

/** Deterministic id of the pre-existing SL2 V1 Locker Thing (idempotent fixture). */
export const SSTV2_EVIDENCE_FIXTURE_V1_LOCKER_ID = "sstv2-evidence-v1-locker";

/** Build the partner path for a step (uuid required for all but create_locker). */
export const sstv2EvidenceStepPath = (
  key: Sstv2EvidenceStepKey,
  uuid: string,
): string => {
  switch (key) {
    case "create_locker":
      return `/mobilus/migration/create_locker`;
    case "history_migration":
      return `/mobilus/migration/history_migration/${uuid}`;
    case "local_migration":
      return `/mobilus/migration/local_migration/${uuid}`;
    case "status_after_operational":
    case "status_after_rollback":
      return `/mobilus/migration/status/${uuid}`;
    case "rollback":
      return `/mobilus/migration/rollback/${uuid}`;
  }
};
